import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  GlobalSuppliersQuery,
  refetchGlobalSuppliersQuery,
  useUpdateGlobalSupplierMutation,
} from '../../generated/graphql';

type Supplier = GlobalSuppliersQuery['globalSuppliers'][number];

interface Props {
  open: boolean;
  supplier: Supplier | null;
  onClose: () => void;
  onUpdated: () => void;
}

export const EditSupplierModal = ({
  open,
  supplier,
  onClose,
  onUpdated,
}: Props) => {
  const [name, setName] = useState('');
  const [emailDomain, setEmailDomain] = useState('');
  const [invoicingEmail, setInvoicingEmail] = useState('');
  const [exceptionFlag, setExceptionFlag] = useState(false);
  const [exceptionReason, setExceptionReason] = useState('');

  useEffect(() => {
    if (supplier) {
      setName(supplier.name);
      setEmailDomain(supplier.emailDomain ?? '');
      setInvoicingEmail(supplier.invoicingEmail ?? '');
      setExceptionFlag(supplier.exceptionFlag);
      setExceptionReason(supplier.exceptionReason ?? '');
    }
  }, [supplier]);

  const [updateGlobalSupplier, { loading: updating }] =
    useUpdateGlobalSupplierMutation({
      refetchQueries: [refetchGlobalSuppliersQuery()],
    });

  const handleSubmit = async () => {
    if (!supplier) return;

    try {
      await updateGlobalSupplier({
        variables: {
          id: supplier.id,
          name: name.trim(),
          emailDomain: emailDomain.trim() || undefined,
          invoicingEmail: invoicingEmail.trim() || undefined,
          exceptionFlag,
          exceptionReason: exceptionReason.trim() || undefined,
        },
        toastConfig: null,
      });
      toast('Supplier updated successfully', { type: 'success' });
      onUpdated();
      onClose();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to update supplier';
      toast(msg, { type: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit Supplier
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            label="Supplier Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
          />

          <TextField
            label="Email Domain"
            value={emailDomain}
            onChange={(e) => setEmailDomain(e.target.value)}
            fullWidth
            size="small"
            helperText="e.g. rohlik.cz — used to detect when integration becomes active"
          />

          <TextField
            label="Invoicing Email"
            value={invoicingEmail}
            onChange={(e) => setInvoicingEmail(e.target.value)}
            fullWidth
            size="small"
            helperText="For suppliers using public email domains (Gmail, Seznam, etc.)"
          />

          <FormControlLabel
            control={
              <Switch
                checked={exceptionFlag}
                onChange={(e) => setExceptionFlag(e.target.checked)}
              />
            }
            label="Exception Flag (cannot use automated email outreach)"
          />

          {exceptionFlag && (
            <TextField
              label="Exception Reason"
              value={exceptionReason}
              onChange={(e) => setExceptionReason(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
              helperText="Explain why this supplier cannot be contacted via automated email"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={updating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={updating}
          startIcon={updating ? <CircularProgress size={16} /> : undefined}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
